import { useCallback, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { GetTasksFilters } from '../../../../../store/meetingSupport/meetingSupport'
import { resetTasks, selectGroupedTasks, selectTasksLoading } from '../../../../../store/task/task'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { User } from '../../../../../models/User'
import { useUserTasks } from '../../../../../views/Tasks/hooks'

enum UserTypes {
  EXTERNAL = 'external',
  MANAGED = 'managed',
  CROSS_ACCOUNT = 'cross-account',
}

const DEFAULT_FILTERS: GetTasksFilters = { type: 'meeting', status: 'open' }

export const useMeetingPrepTasksFetch = (shouldFetch: boolean) => {
  const groupedTasks = useAppSelector(selectGroupedTasks)
  const isLoading = useAppSelector(selectTasksLoading)
  const roomSession = useAppSelector(selectRoomSession)
  const dispatch = useAppDispatch()

  const clients = useMemo(() => roomSession?.users.filter((e) => e.type === UserTypes.EXTERNAL) ?? [], [roomSession?.users])
  const managedUsers = useMemo(
    () => roomSession?.users.filter((e) => e.type === UserTypes.MANAGED || e.type === UserTypes.CROSS_ACCOUNT) ?? [],
    [roomSession?.users],
  )

  const getFetchTasksFilters = useCallback(
    (baseFilters: GetTasksFilters) => {
      if (!shouldFetch) {
        return []
      }

      const filtersList: GetTasksFilters[] = []

      // For each client, create two filter combinations:
      clients.forEach((client: User) => {
        const clientId = client.id

        // 1. Tasks assigned to managed users and related to this client
        managedUsers.forEach((managedUser: User) => {
          filtersList.push({
            ...baseFilters,
            assignedTo: managedUser.id,
            relatedTo: clientId,
          })
        })

        // 2. Tasks assigned to this client
        filtersList.push({
          ...baseFilters,
          assignedTo: clientId,
        })
      })

      return filtersList
    },
    [clients, managedUsers, shouldFetch],
  )

  useUserTasks({
    filters: DEFAULT_FILTERS,
    getFetchTasksFilters,
    taskPageSize: 10,
  })

  useEffect(() => {
    return () => {
      dispatch(resetTasks())
    }
  }, [dispatch])

  return {
    groupedTasks,
    isLoading,
  }
}
